/**
 * Ant Design 5.x 主题配置
 * 基于创美药业 Design Token 系统
 */

import type { ThemeConfig } from 'antd';
import { theme } from 'antd';
import type { ThemeMode } from '../tokens/types';
import { 
  colorTokens, 
  darkColorTokens, 
  antdTokenMapping, 
  darkAntdTokenMapping 
} from '../tokens';

/**
 * 创建 Ant Design 主题配置
 */
export const createAntdTheme = (mode: ThemeMode): ThemeConfig => {
  const isDark = mode === 'dark';
  const currentColorTokens = isDark ? darkColorTokens : colorTokens;
  const currentTokenMapping = isDark ? darkAntdTokenMapping : antdTokenMapping;

  return {
    // 使用 Ant Design 5.x 的算法
    algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm,
    
    // 应用 Design Token 映射
    token: currentTokenMapping,
    
    // 组件级别的主题配置
    components: {
      // 按钮组件
      Button: {
        primaryShadow: 'none',
        defaultShadow: 'none',
        colorPrimary: currentColorTokens.alias.primary,
        colorPrimaryHover: currentColorTokens.alias.primaryHover,
        colorPrimaryActive: currentColorTokens.alias.primaryActive,
        borderRadius: 6,
        controlHeight: 40,
        controlHeightSM: 32,
        controlHeightLG: 48,
        fontWeight: 500,
      },
      
      // 输入框组件
      Input: {
        activeBorderColor: currentColorTokens.alias.primary,
        hoverBorderColor: currentColorTokens.alias.primaryHover,
        colorBgContainer: currentColorTokens.alias.bgContainer,
        colorBorder: currentColorTokens.alias.border,
        borderRadius: 6,
        controlHeight: 40,
        controlHeightSM: 32,
        controlHeightLG: 48,
        paddingInline: 12,
      },
      
      // 选择器组件
      Select: {
        colorBgContainer: currentColorTokens.alias.bgContainer,
        colorBgElevated: currentColorTokens.alias.bgElevated,
        colorBorder: currentColorTokens.alias.border,
        colorPrimary: currentColorTokens.alias.primary,
        borderRadius: 6,
        controlHeight: 40,
        controlHeightSM: 32,
        controlHeightLG: 48,
      },
      
      // 卡片组件
      Card: {
        headerBg: currentColorTokens.alias.bgContainer,
        actionsBg: currentColorTokens.alias.bgContainer,
        colorBgContainer: currentColorTokens.alias.bgContainer,
        colorBorderSecondary: currentColorTokens.alias.borderSecondary,
        borderRadiusLG: 12,
        paddingLG: 24,
        paddingMD: 16,
        paddingSM: 12,
      },
      
      // 菜单组件
      Menu: {
        itemBg: 'transparent',
        subMenuItemBg: 'transparent',
        itemSelectedBg: currentColorTokens.alias.primaryBg,
        itemHoverBg: currentColorTokens.alias.bgElevated,
        itemSelectedColor: currentColorTokens.alias.primary,
        itemColor: currentColorTokens.alias.textSecondary,
        colorBgContainer: currentColorTokens.alias.bgContainer,
        borderRadius: 6,
        itemMarginBlock: 4,
        itemMarginInline: 8,
        itemPaddingInline: 12,
      },
      
      // 表格组件
      Table: {
        headerBg: currentColorTokens.alias.bgContainer,
        rowHoverBg: currentColorTokens.alias.bgElevated,
        colorBgContainer: currentColorTokens.alias.bgContainer,
        colorBorderSecondary: currentColorTokens.alias.borderSecondary,
        borderRadiusLG: 8,
        cellPaddingBlock: 12,
        cellPaddingInline: 16,
      },
      
      // 模态框组件
      Modal: {
        contentBg: currentColorTokens.alias.bgContainer,
        headerBg: currentColorTokens.alias.bgContainer,
        colorBgElevated: currentColorTokens.alias.bgElevated,
        borderRadiusLG: 12,
        paddingLG: 24,
        paddingMD: 16,
      },
      
      // 抽屉组件
      Drawer: {
        colorBgElevated: currentColorTokens.alias.bgContainer,
        colorBgMask: currentColorTokens.alias.bgMask,
        borderRadiusLG: 12,
        paddingLG: 24,
      },
      
      // 下拉菜单组件
      Dropdown: {
        colorBgElevated: currentColorTokens.alias.bgElevated,
        colorBgContainer: currentColorTokens.alias.bgContainer,
        borderRadiusLG: 8,
        paddingBlock: 8,
      },
      
      // 工具提示组件
      Tooltip: {
        colorBgSpotlight: currentColorTokens.alias.bgElevated,
        borderRadius: 6,
        paddingXS: 8,
      },
      
      // 标签页组件
      Tabs: {
        itemColor: currentColorTokens.alias.textSecondary,
        itemSelectedColor: currentColorTokens.alias.primary,
        itemHoverColor: currentColorTokens.alias.primaryHover,
        inkBarColor: currentColorTokens.alias.primary,
        cardBg: currentColorTokens.alias.bgContainer,
        borderRadius: 6,
        horizontalMargin: '0 0 16px 0',
        horizontalItemPadding: '12px 0',
      },
      
      // 分页组件
      Pagination: {
        itemActiveBg: currentColorTokens.alias.primary,
        itemLinkBg: currentColorTokens.alias.bgContainer,
        itemInputBg: currentColorTokens.alias.bgContainer,
        itemSize: 32,
        borderRadius: 6,
      },
      
      // 步骤条组件
      Steps: {
        colorPrimary: currentColorTokens.alias.primary,
        colorText: currentColorTokens.alias.textSecondary,
        colorTextDescription: currentColorTokens.alias.textTertiary,
        navArrowColor: currentColorTokens.alias.textTertiary,
        dotSize: 32,
        iconSize: 16,
      },
      
      // 面包屑组件
      Breadcrumb: {
        itemColor: currentColorTokens.alias.textSecondary,
        lastItemColor: currentColorTokens.alias.textBase,
        linkColor: currentColorTokens.alias.primary,
        linkHoverColor: currentColorTokens.alias.primaryHover,
        separatorColor: currentColorTokens.alias.textTertiary,
        fontSize: 14,
      },
      
      // 标签组件
      Tag: {
        defaultBg: currentColorTokens.alias.bgElevated,
        defaultColor: currentColorTokens.alias.textSecondary,
        borderRadiusSM: 4,
        fontSizeSM: 12,
        lineHeightSM: 1.5,
      },
      
      // 徽章组件
      Badge: {
        colorBgContainer: currentColorTokens.alias.error,
        textFontSize: 12,
        textFontSizeSM: 10,
        textFontWeight: 500,
        indicatorHeight: 20,
        indicatorHeightSM: 16,
      },
      
      // 头像组件
      Avatar: {
        containerSize: 32,
        containerSizeLG: 40,
        containerSizeSM: 24,
        textFontSize: 14,
        textFontSizeLG: 16,
        textFontSizeSM: 12,
        borderRadius: 6,
        groupOverlapping: -8,
        groupBorderColor: currentColorTokens.alias.bgContainer,
      },
      
      // 分割线组件
      Divider: {
        colorSplit: currentColorTokens.alias.borderDivider,
        colorText: currentColorTokens.alias.textTertiary,
        marginLG: 24,
        margin: 16,
        marginXS: 8,
      },
      
      // 锚点组件
      Anchor: {
        linkPaddingBlock: 4,
        linkPaddingInlineStart: 16,
        colorPrimary: currentColorTokens.alias.primary,
        colorSplit: currentColorTokens.alias.borderDivider,
      },
      
      // 回到顶部组件
      BackTop: {
        colorBgElevated: currentColorTokens.alias.bgElevated,
        colorText: currentColorTokens.alias.primary,
        borderRadiusLG: 8,
      },
      
      // 消息提示组件
      Message: {
        colorBgElevated: currentColorTokens.alias.bgElevated,
        borderRadiusLG: 8,
        contentPadding: '12px 16px',
      },
      
      // 通知提醒组件
      Notification: {
        colorBgElevated: currentColorTokens.alias.bgElevated,
        borderRadiusLG: 12,
        paddingMD: 16,
        paddingContentHorizontal: 16,
        paddingContentVertical: 12,
      },
      
      // 警告提示组件
      Alert: {
        colorInfoBg: currentColorTokens.alias.infoBg,
        colorSuccessBg: currentColorTokens.alias.successBg,
        colorWarningBg: currentColorTokens.alias.warningBg,
        colorErrorBg: currentColorTokens.alias.errorBg,
        borderRadiusLG: 8,
        paddingContentHorizontal: 16,
        paddingContentVertical: 12,
      },
      
      // 进度条组件
      Progress: {
        colorInfo: currentColorTokens.alias.primary,
        colorSuccess: currentColorTokens.alias.success,
        remainingColor: currentColorTokens.alias.bgElevated,
        borderRadius: 4,
        lineBorderRadius: 100,
      },
      
      // 骨架屏组件
      Skeleton: {
        gradientFromColor: currentColorTokens.alias.bgElevated,
        gradientToColor: currentColorTokens.alias.bgContainer,
        borderRadiusSM: 4,
      },
      
      // 空状态组件
      Empty: {
        colorTextDisabled: currentColorTokens.alias.textTertiary,
        fontSize: 14,
        fontSizeIcon: 64,
      },
      
      // 结果页组件
      Result: {
        titleFontSize: 24,
        subtitleFontSize: 16,
        iconFontSize: 72,
        extraMargin: '32px 0 0 0',
      },
      
      // 统计数值组件
      Statistic: {
        titleFontSize: 14,
        contentFontSize: 24,
        fontFamily: currentTokenMapping.fontFamily,
      },
      
      // 描述列表组件
      Descriptions: {
        titleColor: currentColorTokens.alias.textBase,
        contentColor: currentColorTokens.alias.textSecondary,
        labelBg: currentColorTokens.alias.bgContainer,
        colorBgContainer: currentColorTokens.alias.bgContainer,
        borderRadiusLG: 8,
        paddingLG: 16,
      },
      
      // 表单组件
      Form: {
        labelColor: currentColorTokens.alias.textBase,
        labelRequiredMarkColor: currentColorTokens.alias.error,
        itemMarginBottom: 24,
        verticalLabelPadding: '0 0 8px',
        labelFontSize: 14,
        labelHeight: 32,
      },
      
      // 图片组件
      Image: {
        colorBgMask: 'rgba(0, 0, 0, 0.45)',
        colorTextLightSolid: '#fff',
      },
      
      // 轮播图组件
      Carousel: {
        dotWidth: 16,
        dotHeight: 3,
        dotActiveWidth: 24,
        arrowSize: 16,
        arrowOffset: 16,
      },
    },
  };
};

/**
 * 获取当前主题的 Ant Design 配置
 */
export const getAntdTheme = (mode: ThemeMode = 'light'): ThemeConfig => {
  return createAntdTheme(mode);
};

/**
 * 默认导出
 */
export default {
  createAntdTheme,
  getAntdTheme,
};